import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import { activeChild, activeChildId, activeThread, messagesFor, threads } from '../store/selectors'
import { Message } from '../components/thread/Message'
import { Composer } from '../components/thread/Composer'
import { ThreadPicker } from '../components/thread/ThreadPicker'
import { EmptyThread } from '../components/thread/EmptyThread'

/**
 * The thread — the substrate the whole app sits on.
 *
 * Everything else (Today, Story, Studio, Library) is a lens on what happens
 * here. If every lens were deleted the app would still work as a conversation;
 * delete this and there is no app. That ordering is the design.
 */
export function ThreadView() {
  const child = useAuroraStore(activeChild)
  const childId = useAuroraStore(activeChildId)
  const thread = useAuroraStore(activeThread)
  const allThreads = useAuroraStore(threads)
  const messages = useAuroraStore((s: any) => messagesFor(s, thread?._id))

  const scrollRef = useRef<HTMLDivElement>(null)
  const [pinned, setPinned] = useState(true)
  const [pickerOpen, setPickerOpen] = useState(false)

  // Stay pinned to the bottom only while the reader is already there. Yanking
  // someone back down mid-scroll is the classic chat sin.
  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    setPinned(distance < 80)
  }

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el && pinned) el.scrollTop = el.scrollHeight
  }, [messages.length, pinned])

  // A streaming reply grows continuously; follow it only if pinned.
  const lastContent = (messages[messages.length - 1] as any)?.content
  useEffect(() => {
    const el = scrollRef.current
    if (el && pinned) el.scrollTop = el.scrollHeight
  }, [lastContent, pinned])

  if (!child || !childId) return null

  const send = (content: string, artifactIds?: string[]) => {
    ;(auroraStore.getState() as any).sendMessage({
      childId,
      threadId: thread?._id,
      content,
      artifactIds,
    })
    setPinned(true)
  }

  return (
    <div className="thread">
      <div className="thread-bar">
        <button
          className="thread-bar-title"
          onClick={() => setPickerOpen((v) => !v)}
          aria-expanded={pickerOpen}
        >
          <span className="thread-bar-name">{thread?.title ?? 'New thread'}</span>
          <ChevronIcon />
        </button>
        <button
          className="thread-bar-new"
          onClick={() => (auroraStore.getState() as any).newThread(childId)}
          aria-label="Start a new thread"
        >
          <PlusIcon />
        </button>
      </div>

      {pickerOpen && (
        <ThreadPicker
          threads={allThreads}
          activeId={thread?._id}
          onPick={(id) => {
            ;(auroraStore.getState() as any).selectThread(id)
            setPickerOpen(false)
            setPinned(true)
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <div className="thread-scroll" ref={scrollRef} onScroll={onScroll}>
        <div className="thread-inner">
          {messages.length === 0 ? (
            <EmptyThread childName={child.name} onPick={send} />
          ) : (
            messages.map((m: any, i: number) => (
              <Message key={m._id} message={m} isLast={i === messages.length - 1} />
            ))
          )}
        </div>
      </div>

      {!pinned && messages.length > 0 && (
        <button
          className="thread-jump"
          onClick={() => {
            const el = scrollRef.current
            if (el) el.scrollTop = el.scrollHeight
            setPinned(true)
          }}
        >
          Jump to latest
        </button>
      )}

      <Composer onSend={send} />
    </div>
  )
}

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
)

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
)
